import type { CourierSettings } from '@shared/sdk-v1';

/**
 * The set of `CourierSettings` flags that enable a warehouse "Import Latest"
 * integration. Keep this in sync with the dispatchers in
 * `hooks/warehouses/useWarehouseImport.ts`.
 */
export const WAREHOUSE_API_ENABLED_FLAGS = [
  'isSpeedzMasterV2ApiEnabled',
  'isJaShippersApiEnabled',
  'isLogisApiEnabled',
  'isShipCorpApiEnabled',
  'isShipBizApiEnabled',
  'isHnmApiEnabled',
  'isCargoJaApiEnabled',
  'isFarin2YaadApiEnabled',
  'isAtoraApiEnabled',
  'isOnePlusApiEnabled',
  'isTrGlobalApiEnabled',
] as const satisfies ReadonlyArray<keyof CourierSettings>;

/**
 * Returns true when the courier has at least one warehouse import integration
 * enabled. Used to gate the Warehouse menu item, the import action, and the
 * warehouse filter UI so they stay consistent and don't hide the feature for
 * couriers on a non-SpeedzMasterV2 warehouse (e.g. Farin2Yaad-only).
 */
export const hasAnyWarehouseApiEnabled = (
  settings?: Partial<CourierSettings> | null
): boolean => {
  if (!settings) return false;
  return WAREHOUSE_API_ENABLED_FLAGS.some((flag) => Boolean(settings[flag]));
};

/**
 * Integrations that `customerService.syncWithWarehouses` can push customers to
 * without any input — one click on Customers → "Sync with warehouse".
 * Keep in sync with the branches in that function.
 */
export const PUSH_CUSTOMER_SYNC_FLAGS = [
  'isLogisApiEnabled',
  'isShipCorpApiEnabled',
  'isShipBizApiEnabled',
  'isCargoJaApiEnabled',
  'isSsnApiEnabled',
  'isTrGlobalApiEnabled',
] as const satisfies ReadonlyArray<keyof CourierSettings>;

/**
 * Every integration the "Sync with warehouse" button serves. OnePlus is extra:
 * useCustomers pushes it through its own endpoint (with the stored OnePlus API
 * key) so it can report counts and explain a rejected key.
 */
export const CUSTOMER_SYNC_FLAGS = [
  ...PUSH_CUSTOMER_SYNC_FLAGS,
  'isOnePlusApiEnabled',
] as const satisfies ReadonlyArray<keyof CourierSettings>;

const anyFlag = (
  flags: ReadonlyArray<keyof CourierSettings>,
  settings?: Partial<CourierSettings> | null
): boolean => {
  if (!settings) return false;
  return flags.some((flag) => Boolean(settings[flag]));
};

/** Whether to show Customers → "Sync with warehouse". */
export const hasAnyCustomerSyncEnabled = (
  settings?: Partial<CourierSettings> | null
): boolean => anyFlag(CUSTOMER_SYNC_FLAGS, settings);

/** Whether `syncWithWarehouses` has anything to push to. */
export const hasAnyPushCustomerSyncEnabled = (
  settings?: Partial<CourierSettings> | null
): boolean => anyFlag(PUSH_CUSTOMER_SYNC_FLAGS, settings);
